/**
 * @file productIcons.tsx
 * @brief Shared Lucide icons for billable product keys in report tables.
 *
 * @author DES
 * @date 28-07-2026
 * @updated 03-10-2026: API product keys mapped, nav icons, no circle
 */

import {
  CreditCard,
  Languages,
  type LucideIcon,
  PenLine,
} from "lucide-react";
import { BoardroomTable } from "@/components/icons/BoardroomTable";

// The same icons as the side nav (03-10-2026). The reports API sends
// `conversation` / `boardroom` / `note` / `subscription`; only `boardroom`
// was mapped, so Interpretation and Personal Notes fell back to the card.
const PRODUCT_ICONS: Record<string, LucideIcon> = {
  conversation: Languages,
  interpretation: Languages,
  boardroom: BoardroomTable, // DES 08-10-2026: app icon, was Presentation
  note: PenLine, // DES 08-10-2026: app icon, was NotebookPen
  notes: PenLine, // DES 08-10-2026: app icon, was NotebookPen
  personal_notes: PenLine, // DES 08-10-2026: app icon, was NotebookPen
  subscription: CreditCard,
  plan_seats: CreditCard,
  plan: CreditCard,
};

// Module-private: no external consumer, and a value export beside a component
// costs the module its Fast Refresh. (02-08-2026)
function productIcon(product: string | null | undefined): LucideIcon {
  if (!product) return CreditCard;
  return PRODUCT_ICONS[product] ?? CreditCard;
}

export function ProductLabelCell({
  product,
  label,
}: {
  product: string;
  label: string;
}) {
  const Icon = productIcon(product);
  return (
    // A bare red icon, no tinted circle (DES 03-10-2026).
    <span className="inline-flex items-center gap-2.5 font-medium">
      <Icon className="h-4 w-4 shrink-0 text-brand" aria-hidden />
      {label}
    </span>
  );
}
